import BoltIcon from '@mui/icons-material/Bolt';
import Co2Icon from '@mui/icons-material/Co2';
import LensBlurIcon from '@mui/icons-material/LensBlur';
import OpacityIcon from '@mui/icons-material/Opacity';
import ThermostatIcon from '@mui/icons-material/Thermostat';
import TroubleshootIcon from '@mui/icons-material/Troubleshoot';
import WavesIcon from '@mui/icons-material/Waves';
import { Button, Divider, Grid, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { ReactNode, useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAppSelector } from '@app/hooks';
import { RootState } from '@app/store';
import { Category, Measure } from '@lib/labels';
import { CompareLabel, Interval } from '../types';
import MetricCard from './MetricCard';
import PerformanceChart from './PerformanceChart';

type MetricDef = {
  category: Category;
  measure: Measure;
  icon: ReactNode;
};

const METRICS: MetricDef[] = [
  { category: Category.air, measure: Measure.temperature, icon: <ThermostatIcon fontSize="small" color="action" /> },
  { category: Category.air, measure: Measure.humidity, icon: <OpacityIcon fontSize="small" color="action" /> },
  { category: Category.air, measure: Measure.co2, icon: <Co2Icon fontSize="small" color="action" /> },
  { category: Category.air, measure: Measure.pm2_5, icon: <LensBlurIcon fontSize="small" color="action" /> },
  { category: Category.water, measure: Measure.usage_total, icon: <WavesIcon fontSize="small" color="action" /> },
  { category: Category.power, measure: Measure.usage_rate, icon: <BoltIcon fontSize="small" color="action" /> },
];

const INTERVAL_OPTIONS = [
  { label: '1W', value: Interval.WEEK },
  { label: '1M', value: Interval.MONTH },
  { label: '3M', value: Interval.QUARTER },
  { label: '1Y', value: Interval.YEAR },
];

const PerformanceDashboard = () => {
  const home = useAppSelector((state: RootState) => state.property);
  const propertyId = useMemo(() => home?.property_id || '', [home?.property_id]);

  const [selectedIdx, setSelectedIdx] = useState<number | null>(null);
  const [interval, setInterval] = useState<Interval>(Interval.WEEK);

  const handleCardClick = useCallback((idx: number) => {
    setSelectedIdx(prev => prev === idx ? null : idx);
  }, []);

  const selectedMetric = selectedIdx !== null ? METRICS[selectedIdx] : null;

  return (
    <Stack spacing={3}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography variant="h6">Current Conditions</Typography>
        {propertyId && (
          <Tooltip title="Open advanced charts" placement="top">
            <IconButton component={Link} to={`/homes/${propertyId}/charts`} target="_blank" size="small">
              <TroubleshootIcon color="info" />
            </IconButton>
          </Tooltip>
        )}
      </Stack>

      <Grid container spacing={2}>
        {METRICS.map((m, idx) => (
          <Grid item xs={12} sm={6} md={4} key={`${m.category}-${m.measure}`}>
            <MetricCard
              category={m.category}
              measure={m.measure}
              icon={m.icon}
              onClick={() => handleCardClick(idx)}
              selected={selectedIdx === idx}
            />
          </Grid>
        ))}
      </Grid>

      {selectedMetric && (
        <>
          <Divider />
          <Stack direction="row" spacing={1}>
            {INTERVAL_OPTIONS.map(opt => (
              <Button
                key={opt.value}
                size="small"
                variant={interval === opt.value ? 'contained' : 'outlined'}
                onClick={() => setInterval(opt.value)}
              >
                {opt.label}
              </Button>
            ))}
          </Stack>
          <PerformanceChart
            category={selectedMetric.category}
            measure={selectedMetric.measure}
            interval={interval}
            compare={CompareLabel.devices}
          />
        </>
      )}
    </Stack>
  );
};

export default PerformanceDashboard;
